/**
 * Custom-column cell for `button` columns. Renders an action button whose label
 * is the column's configured caption. Clicking calls `onPress` — the row writes
 * a fresh timestamp nonce to the cell, which the automations engine treats as a
 * `button_clicked` trigger. The stored value is never shown; the cell always
 * renders the caption.
 *
 * Clicking shows a brief "running" state (spinner + disabled) so the user gets
 * immediate feedback while the automation processes in the background worker —
 * the board reflects the result on the next refetch (~1–2s). The state clears
 * itself after a short window (it's optimistic UI, not tied to the actual job).
 */
import { Button, Spinner } from '@monksflow/monks-ui';
import { Check } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ButtonCustomCellProps {
  caption: string;
  onPress: () => void;
  disabled?: boolean;
}

/** How long the spinner/confirmation stays up after a click (covers the
 *  automation worker + the board's delayed refetch). */
const RUNNING_MS = 1200;
const DONE_MS = 900;

export function ButtonCustomCell({ caption, onPress, disabled = false }: ButtonCustomCellProps) {
  const { t } = useTranslation('trackers');

  const [phase, setPhase] = useState<'idle' | 'running' | 'done'>('idle');
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout);
    },
    [],
  );

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (phase === 'running') return;
    onPress();
    setPhase('running');
    timers.current.push(
      setTimeout(() => setPhase('done'), RUNNING_MS),
      setTimeout(() => setPhase('idle'), RUNNING_MS + DONE_MS),
    );
  };

  return (
    <div className="flex w-full items-center justify-center px-1">
      <Button
        variant="outline"
        size="sm"
        className="h-7 w-full gap-1.5 truncate text-xs"
        disabled={disabled || phase === 'running'}
        onClick={handleClick}
      >
        {phase === 'running' ? (
          <Spinner size="sm" aria-hidden="true" />
        ) : phase === 'done' ? (
          <Check className="size-3.5 text-emerald-500" aria-hidden="true" />
        ) : null}
        {caption || t('trackersBoard.buttonCustomCell.run')}
      </Button>
    </div>
  );
}
